import uniqid from "uniqid";
import { storage } from "api/firebase";
import {
  IMAGE_COMPRESSION_MAX_SIZE_MB,
  compressImage,
} from "tools/file/compressImage";
import { IRichTextImageAdapter, IRichTextPendingImage } from "./types";

const FALLBACK_IMAGE_PATH = "rich-text";

// Extracted from the pre-adapter handleAddImage (index.tsx) which uploaded
// straight to Firebase Storage at insert time.
export class FirebaseImageAdapter implements IRichTextImageAdapter {
  readonly immediate = true;

  // The old inline `handleAddImage` interpolated `${imagePath}/...` directly,
  // so a call site that never passed `imagePath` silently uploaded under the
  // literal "undefined/" prefix. Every remaining Firebase call site now passes
  // an explicit path (all the others moved to `storageServiceImageAdapter`),
  // so the legacy behaviour has no callers left to preserve — fall back to a
  // named bucket folder instead of writing "undefined/" ever again.
  constructor(private readonly imagePath: string = FALLBACK_IMAGE_PATH) {}

  async insert({
    blob,
    extension,
    scale,
  }: IRichTextPendingImage): Promise<string> {
    // Compressed here rather than at pick time so it happens once, right
    // before the bytes actually leave the browser — that covers every way an
    // image reaches the document (toolbar pick, crop, paste, drop), not just
    // the toolbar. `compressImage` never throws: on failure, or when the
    // result isn't smaller, it hands back the original file.
    const file = await compressImage(
      new File([blob], `${uniqid()}.${extension}`, {
        type: blob.type || `image/${extension}`,
      }),
      IMAGE_COMPRESSION_MAX_SIZE_MB.taskDescription
    );

    // `file.name` — not the pre-compression name: WebP conversion rewrites the
    // extension, and the stored object has to match its real content type.
    const fileRef = storage.ref().child(`${this.imagePath}/${file.name}`);
    await fileRef.put(file);
    const downloadUrl: string = await fileRef.getDownloadURL();
    return scale ? `${downloadUrl}&scale=${scale}` : downloadUrl;
  }
}
